import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('cart-icon:sync-required', this.#onSyncRequired);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();

    // FIX: Install a global fetch interceptor so that any /cart/add,
    // /cart/change, or /cart/update call triggers a badge refresh, even when
    // the theme's ThemeEvents.cartUpdate event fails to reach this component
    // (which happens on the homepage collection grid).
    CartIcon.#installFetchInterceptor();

    // Sync once on load in case the badge is stale from a prior state.
    this.#syncFromServer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('cart-icon:sync-required', this.#onSyncRequired);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.#syncFromServer();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
    this.#syncFromServer();
  };

  #onSyncRequired = () => {
    // Small delay so server-side cart state has settled after the mutation.
    setTimeout(() => this.#syncFromServer(), 50);
  };

  /**
   * Installs a one-time global fetch wrapper that dispatches a
   * `cart-icon:sync-required` event whenever a cart-mutating fetch completes.
   */
  static #installFetchInterceptor() {
    if (window.__cartIconFetchPatched) return;
    window.__cartIconFetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    /** @param {unknown} url */
    const isCartMutation = (url) =>
      typeof url === 'string' &&
      (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'));

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const input = args[0];
        /** @type {string} */
        let url = '';
        if (typeof input === 'string') url = input;
        else if (input instanceof Request) url = input.url;
        else if (input && typeof input === 'object' && 'url' in input) url = String(input.url);

        if (isCartMutation(url) && response.ok) {
          document.dispatchEvent(new CustomEvent('cart-icon:sync-required'));
        }
      } catch (_) {
        // no-op
      }
      return response;
    };
  }

  /**
   * Fetches the actual cart from the server and updates the badge to match.
   */
  #syncFromServer = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart?.item_count ?? 0);
      if (Number.isNaN(count)) return;

      if (!this.refs.cartBubbleCount || !this.refs.cartBubble) return;

      const wasZero = this.currentCartCount === 0;
      this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
      this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);
      this.currentCartCount = count;
      this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({ value: String(count), timestamp: Date.now() })
      );

      // Play the add animation when going from empty → non-empty.
      if (wasZero && count > 0) {
        try {
          await new Promise((resolve) => requestAnimationFrame(resolve));
          this.refs.cartBubble.classList.add('cart-bubble--animating');
          await onAnimationEnd(this.refs.cartBubbleText);
          this.refs.cartBubble.classList.remove('cart-bubble--animating');
        } catch (_) {
          this.refs.cartBubble.classList.remove('cart-bubble--animating');
        }
      }
    } catch (_) {
      // network error — leave badge as-is
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
